import "../ModalEditor.css";
import { useComponentEditorContext } from "../ComponentEditorContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Input, Modal, SelectChangeEvent, Snackbar, Typography } from "@mui/material";
import { PositionsCanvasGrid } from "./PositionsCanvasGrid";
import { useModalEditorDisplayContext } from "../ModalEditorContext";
import { CoordsData, dimsValueDefault, PositionObject, PositionsData } from "./PositionsData";
import { getPositionObjectNames, reducePathUntilKeyword } from "../Helpers";
import { conditionedAttribute } from "../Conditional/HelpersConditional";
import { useImmer } from "use-immer";
import { ZoomControls } from "../ZoomControls";
import { DotsSizeSelector } from "./DotsSizeSelector";
import { MultiAndGapSizeSettings } from "./MultiAndGapSizeSettings";
import { PositionSetEditor } from "./PositionSetEditor";
import { BackgroundSelector } from "../BackgroundSelector";
import { DimsEditor } from "./DimsEditor";
import { ScaleMultEditor } from "./ScaleMultEditor";
import { DirectionEditor } from "./DirectionEditor";
import { ContainerSizeInfo } from "../ContainerSizeInfo";
import { AcceptsEditor } from "./AcceptsEditor";
import { CoordsArray } from "./CoordsArray";
import ConditionalMiddleware from "../Conditional/ConditionalMiddleware";
import { useMeasureElement, useObjectImages } from "../CustomHooks";
import { TreeModifyContext, useTreeDataContext, useTreeModifyContext } from "../TreeDataContext";
import { ImageInfo } from "../ImageInfo";
import ReactDOM from "react-dom";
import { PathInfo } from "../PathInfo";
import { PropertyGroup } from "../PropertyGroup";
import { SaveAndCloseButton } from "../SaveAndCloseButton";

const bannedPositionSetNames = ["dims"];

export const PositionsEditor: React.FC<{
  showModal: boolean;
  modalContextKey: string[];
  modalContextValue: any;
}> = ({ showModal, modalContextKey, modalContextValue }) => {
  const { onCloseModal } = useModalEditorDisplayContext();
  const { saveChangesAtPath, addGamePiece } = useTreeModifyContext();
  const { projectName, projectInfo } = useComponentEditorContext();
  const { treeData } = useTreeDataContext();
  const [zoom, setZoom] = useState(1);
  const [myModalContextValue, setMyModalContextValue] = useImmer<PositionsData>(modalContextValue as PositionsData);
  const { possibleBackgroundImages, selectedBackgroundImage, setSelectedBackgroundImage } = useObjectImages(
    modalContextKey,
    treeData,
    projectName,
    projectInfo,
  );
  const [positionsEditorTarget, setPositionsEditorTarget] = useState<string>("");
  const [dotsSizeMultiplier, setDotsSizeMultiplier] = useState(1);
  const [containerSize, setContainerSize] = useState<ContainerSizeInfo>({ width: 0, height: 0 });
  const [canvasGridContainer, setCanvasGridContainer] = useState<HTMLDivElement>();
  const canvasContainerSizeInfo = useMeasureElement(canvasGridContainer, 1);
  const [errorBar, setErrorBar] = useState({ message: "", state: false });
  const [anyChangesMade, setAnyChangesMade] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (anyChangesMade) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [anyChangesMade, showModal]);

  const selectedPositionObject = useMemo(() => {
    if (
      positionsEditorTarget !== "" &&
      myModalContextValue !== undefined &&
      myModalContextValue[positionsEditorTarget] !== undefined
    ) {
      return myModalContextValue[positionsEditorTarget] as PositionObject;
    }
    return undefined;
  }, [myModalContextValue, positionsEditorTarget]);

  const handleResize = useCallback(() => {
    if (canvasGridContainer !== undefined) {
      setContainerSize({ width: canvasGridContainer.scrollWidth, height: canvasGridContainer.scrollHeight });
    }
  }, [canvasGridContainer]);

  const measuredRef = useCallback((node: any) => {
    if (node === null) return;
    setCanvasGridContainer(node);
  }, []);

  useEffect(() => {
    handleResize();
  }, [canvasContainerSizeInfo.getSizeInfo, handleResize]);

  useEffect(() => {
    if (positionsEditorTarget === "" && getPositionObjectNames(myModalContextValue).length > 0) {
      setPositionsEditorTarget(getPositionObjectNames(myModalContextValue)[0]);
    }
  }, [myModalContextValue, positionsEditorTarget, showModal]);

  useEffect(() => {
    handleResize();
  }, [handleResize, positionsEditorTarget]);

  const handleSelectedPositionSetChange = (event: SelectChangeEvent) => {
    setPositionsEditorTarget(event.target.value);
  };

  const handleBackgroundChange = (event: SelectChangeEvent) => {
    setSelectedBackgroundImage(event.target.value);
  };

  function handleCloseModal() {
    saveChangesAtPath(myModalContextValue, modalContextKey);
    onCloseModal();
  }

  function handleAddPositionSet(newSetName: string) {
    if (newSetName === "") {
      setErrorBar({ message: "Name cannot be empty", state: true });
      return;
    }
    if (bannedPositionSetNames.includes(newSetName)) {
      setErrorBar({ message: "Name is reserved", state: true });
      return;
    }
    if (myModalContextValue[newSetName] !== undefined) {
      setErrorBar({ message: "Name already exists", state: true });
      return;
    }
    const updatedValue = { ...myModalContextValue, [newSetName]: { coords: [] } };
    setMyModalContextValue(updatedValue);
    setPositionsEditorTarget(newSetName);
    setAnyChangesMade(true);
  }

  function handleDeletePositionSet() {
    if (positionsEditorTarget === "") return;
    const updatedValue = { ...myModalContextValue };
    delete updatedValue[positionsEditorTarget];
    setMyModalContextValue(updatedValue);
    setPositionsEditorTarget(getPositionObjectNames(updatedValue)[0]);
    setAnyChangesMade(true);
  }

  const handleCloseErrorBar = () => {
    setErrorBar({ message: "", state: false });
  };

  function handleRenamePositionSet(oldName: string, newName: string) {
    if (oldName === newName) return;
    if (newName === "") {
      setErrorBar({ message: "Name cannot be empty", state: true });
      return;
    }
    if (bannedPositionSetNames.includes(newName)) {
      setErrorBar({ message: "Name is reserved", state: true });
      return;
    }
    if (myModalContextValue[newName] !== undefined) {
      setErrorBar({ message: "Name already exists", state: true });
      return;
    }
    if (myModalContextValue[newName]?.startsWith("__")) {
      setErrorBar({ message: 'Cannot start with "__"', state: true });
      return;
    }
    setMyModalContextValue((draft) => {
      draft[newName] = draft[oldName];
      delete draft[oldName];
    });
    setPositionsEditorTarget(newName);
    setAnyChangesMade(true);
  }

  const backgroundImageValue = useMemo(() => {
    if (selectedBackgroundImage) {
      return possibleBackgroundImages.find(
        (image) =>
          (image.type === "src" && image.value === selectedBackgroundImage) ||
          (image.type === "reactImg" && image.name === selectedBackgroundImage),
      );
    }
    return { type: "src", value: "empty" } as ImageInfo;
  }, [possibleBackgroundImages, selectedBackgroundImage]);

  const setAtPathInPositions = useCallback(
    (value: any, path: string[]) => {
      setMyModalContextValue((draft) => {
        let reducedPath = reducePathUntilKeyword(path, "positions");
        let currentNode = draft;
        // Traverse the reduced path to get to the correct node, creating nodes as needed
        for (let i = 0; i < reducedPath.length - 1; i++) {
          const pathKey = reducedPath[i];
          if (!currentNode[pathKey]) {
            currentNode[pathKey] = {}; // Initialize a new node if it doesn't exist
          }
          currentNode = currentNode[pathKey];
        }

        // Set the new value at the last key of the reduced path
        currentNode[reducedPath[reducedPath.length - 1]] = value;
      });
      setAnyChangesMade(true);
    },
    [setMyModalContextValue],
  );

  const removeAtPathInPositions = useCallback(
    (path: string[]) => {
      setMyModalContextValue((draft) => {
        if (!draft) {
          return;
        }
        let currentNode = draft;
        let reducedPath = reducePathUntilKeyword(path, "positions");
        // Traverse the path to get to the correct node
        for (let i = 0; i < reducedPath.length - 1; i++) {
          let pathKey = reducedPath[i];
          if (!currentNode[pathKey]) {
            return; // Return if the path doesn't exist
          }
          currentNode = currentNode[pathKey];
        }

        const lastKey = reducedPath[reducedPath.length - 1];
        if (Array.isArray(currentNode) && !isNaN(Number(lastKey))) {
          currentNode.splice(Number(lastKey), 1);
        } else {
          delete currentNode[lastKey];
        }
      });
      setAnyChangesMade(true);
    },
    [setMyModalContextValue],
  );

  const treeSaveContext = useMemo(
    () => ({
      saveChangesAtPath: setAtPathInPositions,
      removeAtPath: removeAtPathInPositions,
      addGamePiece,
    }),
    [addGamePiece, removeAtPathInPositions, setAtPathInPositions],
  );

  if (!showModal) {
    return null;
  }

  return (
    <Modal
      open={showModal}
      onClose={handleCloseModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <TreeModifyContext.Provider value={treeSaveContext}>
        <Box className="large-modal">
          <Box className="editor-layout">
            <Box className="control-panel control-panel-layout">
              <ZoomControls setZoom={setZoom} />
              <BackgroundSelector
                value={selectedBackgroundImage}
                onChange={handleBackgroundChange}
                possibleBackgroundImages={possibleBackgroundImages}
              />
              <PropertyGroup>
                <ConditionalMiddleware
                  propertyName={"dims"}
                  conditions={conditionedAttribute(myModalContextValue, "dims", [dimsValueDefault, dimsValueDefault])}
                  originalComponent={(dimsValue: number[], conditionalPropName: string) => {
                    let dimsCondResult = [];
                    let coordsTarget = document.getElementById("coords-array-mount-point");
                    if (selectedPositionObject && coordsTarget) {
                      dimsCondResult.push(
                        ReactDOM.createPortal(
                          <PathInfo key={"coords-editor-path-key"} name={positionsEditorTarget}>
                            <ConditionalMiddleware
                              propertyName={"coords"}
                              conditions={conditionedAttribute(selectedPositionObject, "coords")}
                              spreadControls={true}
                              originalComponent={(conditionedCoord: CoordsData, coordPath: string[]) => {
                                let result = [];
                                let positionsTarget = document.getElementById("positions-grid-mount-point");
                                if (positionsTarget) {
                                  result.push(
                                    ReactDOM.createPortal(
                                      <PathInfo key={"position-canvas-grid-path-key"} name={coordPath}>
                                        <PositionsCanvasGrid
                                          backgroundImage={backgroundImageValue}
                                          nodeState={myModalContextValue}
                                          dims={dimsValue}
                                          zoom={zoom}
                                          containerSize={containerSize}
                                          coords={conditionedCoord}
                                          dotsSizeMultiplier={dotsSizeMultiplier}
                                          targetSetName={positionsEditorTarget}
                                        />
                                      </PathInfo>,
                                      positionsTarget,
                                    ),
                                  );
                                }
                                result.push(
                                  <PathInfo key={"coords-array-path-key"} name={coordPath}>
                                    <CoordsArray key={"coords-array"} coords={conditionedCoord} />
                                  </PathInfo>,
                                );
                                return result;
                              }}
                            />
                          </PathInfo>,
                          coordsTarget,
                        ),
                      );
                    }

                    dimsCondResult.push(
                      <PathInfo key={"dims-editor-path-key"} name={conditionalPropName}>
                        <DimsEditor value={dimsValue || [dimsValueDefault, dimsValueDefault]} />
                      </PathInfo>,
                    );
                    return dimsCondResult;
                  }}
                />
              </PropertyGroup>
              <PropertyGroup additionalClasses={"control-panel-layout"}>
                <PositionSetEditor
                  currentPositionSet={positionsEditorTarget}
                  onChange={handleSelectedPositionSetChange}
                  positionSetNames={getPositionObjectNames(myModalContextValue)}
                  onTryAddPositionSet={handleAddPositionSet}
                  onTryRemovePositionSet={handleDeletePositionSet}
                  onTryRenamePositionSet={handleRenamePositionSet}
                />
                <Box borderBottom={"2px solid #000"} />
                {selectedPositionObject && (
                  <PathInfo name={positionsEditorTarget}>
                    <DotsSizeSelector setDotSize={setDotsSizeMultiplier} />
                    <AcceptsEditor accepts={selectedPositionObject.accepts || []} />
                    <MultiAndGapSizeSettings
                      multi={selectedPositionObject.multi || false}
                      gapSize={selectedPositionObject.gapSize || 0}
                      maxSpread={selectedPositionObject.maxSpread || 0}
                    />
                    <Typography>Hoverzoom</Typography>
                    <DirectionEditor dir={selectedPositionObject.multiDirection || 0} />
                    <Input
                      type="number"
                      size="small"
                      id="hoverZoom"
                      value={selectedPositionObject.hoverZoom || 1}
                      onChange={(event) => {
                        setMyModalContextValue((draft) => {
                          if (!draft[positionsEditorTarget]) return;
                          draft[positionsEditorTarget].hoverZoom = parseFloat(event.target.value);
                        });
                      }}
                      slotProps={{ input: { type: "number", min: 0, max: 10, step: 0.1 } }}
                    />
                    <PropertyGroup>
                      <ConditionalMiddleware
                        propertyName={"scaleMult"}
                        conditions={conditionedAttribute(selectedPositionObject, "scaleMult", 1)}
                        originalComponent={(scaleMultValue: number, conditionalPropPath: string) => {
                          return (
                            <PathInfo key="scaleMult" name={conditionalPropPath}>
                              <ScaleMultEditor scaleMult={scaleMultValue} />
                            </PathInfo>
                          );
                        }}
                      />
                    </PropertyGroup>
                    <PropertyGroup id={"coords-array-mount-point"} />
                  </PathInfo>
                )}
              </PropertyGroup>
              <Box className="flex-filler"></Box> {/* Spacer to fill empty space*/}
              <SaveAndCloseButton onClick={handleCloseModal} />
            </Box>
            <Box ref={measuredRef} className="canvas-grid-container" id="positions-grid-mount-point"></Box>
          </Box>
          <Snackbar
            anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            open={errorBar.state}
            onClose={handleCloseErrorBar}
            message={errorBar.message}
            key={errorBar.message}
            autoHideDuration={5000}
          >
            <Alert onClose={handleCloseErrorBar} severity="error" variant="filled" sx={{ width: 1 }}>
              {errorBar.message}
            </Alert>
          </Snackbar>
        </Box>
      </TreeModifyContext.Provider>
    </Modal>
  );
};
